import { useRef, useState} from "react";
import styled from "styled-components";
import classNames from "classnames";
import Person from "@material-ui/icons/Person";
import ExitToApp from "@material-ui/icons/ExitToApp";
import MapIcon from '@material-ui/icons/Room';
import { InertiaLink, usePage } from "@inertiajs/inertia-react";
import { Container, Popover, Button } from "@material-ui/core";
import Grid from "@material-ui/core/Grid";
import Cookies from "js-cookie";
import { useSelector } from "@xstate/store/react";
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';
import { Inertia } from "@inertiajs/inertia";

import { Trans } from "../../utils/i18n";
import { useLanguageSwitch } from "../../hooks/useLanguageSwitch";
import LanguageModal from "../LanguageModal";
import {getFlagEmoji} from "../../utils/getFlagEmoji";
import FlashMessage from "../FlashMessage";
import { ShoppingCartContainer } from "../ShoppingCartContainer";
import { useShoppingCartStore } from "../../stores/ShoppingCartStore";
import ClientSwitcher from "../ClientSwitcher";

const Nav = styled.nav`
    width: 100%;
    background-color: ${(props) => props.color || props.theme.primary};
    padding: 15px;
    font-size: 30px;
    box-shadow: 0px 7px 15px -2px rgba(255, 255, 255, 0.6);
`;

const PopoverContent = styled.div`
    padding: 0 20px;
    border-radius: 10px;
`;

const Footer = styled.footer`
    background-color: ${(props) => props.theme.primary};
    color: ${(props) => props.theme.headericons};
    font-size: 14px;
    padding: 60px;
`;

const FooterLogos = styled.div`
    background-color: #1a1a1a;
    color: ${(props) => props.theme.headericons};
    padding: 60px;
    text-align: center;
`;

export default function NiuLayout({ children, wrapperStyle }) {
    const {
        props: { flash, user, languages, availableCountries, client, currentLanguage, currentCountry, },
    } = usePage();
    const { updateLanguage } = useLanguageSwitch();
    const shoppingCartRef = useRef(null);
    const [isShoppingCartOpen, setIsShoppingCartOpen] = useState(false);
    const shoppingCartStore = useShoppingCartStore();
    const shoppingCartProducts = useSelector(
        shoppingCartStore,
        (state) => state.context.products
    );

    const handleLogoutClick = async () => {
        Inertia.post(route("logout"), undefined, {
            onSuccess: () => {
                shoppingCartStore.send({
                    type: "clearCart"
                });
            },
        });
    };
    const [langModalOpen, setLangModalOpen] = useState(!Cookies.get("country"));
    const [langSwitchElement, setLangSwitchElement] = useState();
    const open = Boolean(langSwitchElement);

    const handleLangClick = (event) => {
        if(languages.filter(item => item.code !== currentLanguage).length === 0) {
            return;
        }
        if(open) {
            setLangSwitchElement(null);
            return;
        }
        setLangSwitchElement(event.currentTarget);
    };

    const handlePopoverClose = () => {
        setLangSwitchElement(null);
    };

    const handleLanguageChange = (language) => {
        setLangSwitchElement(null);
        updateLanguage(language);
    };

    const country = availableCountries.find((item) => item.code === currentCountry);

    return (
        <div className={'flex flex-col min-h-screen'}>
            <Nav color={"" || children.props.client.brand_color_header} id={'top'}>
                <Container maxWidth="lg">
                    <Grid container spacing={3}>
                        <div className="flex flex-1 flex-row p-2 flex-wrap justify-start sm:justify-start">
                            <InertiaLink href={'/'} className={'flex no-underline focus:no-underline active:no-underline'}>
                                {children.props.client.brand_logo && (
                                        <img className={'w-auto h-12 mr-3 py-1 px-0 hover:opacity-70'}
                                            src={children.props.client.brand_logo}
                                        />
                                )}
                            </InertiaLink>
                            <div className={'text-white flex-col text-sm hidden lg:flex cursor-pointer hover:text-gray-200'}
                                 onClick={() => {
                                     setLangModalOpen(true);
                                 }}
                            >
                                <span className={'text-sm pl-6'}>Niu Scooter Repair</span>
                                <span className={'text-base font-bold flex items-center'}>
                                    <MapIcon />
                                    <span>{ country.name }</span>
                                </span>
                            </div>
                        </div>
                        <div className={'flex items-center justify-center select-none'}>
                            {!children.props.user?.id && (
                                <InertiaLink href={route('login')} className={'text-white text-sm mr-3 no-underline hover:opacity-50'}>
                                    Login
                                </InertiaLink>
                                )
                            }
                            <div className={'flex items-center justify-center cursor-pointer hover:opacity-70'} onClick={handleLangClick}>
                                <span className={'text-4xl'}>
                                    { getFlagEmoji(currentLanguage) }
                                </span>
                                <div className={classNames({
                                    'transform rotate-180 -mt-2': open,
                                    'hidden': languages.filter(item => item.code !== currentLanguage).length === 0,
                                })}>
                                    <svg width="25" height="24" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M6.48633 9L12.4863 15L18.4863 9" stroke="#DBDBDB" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                </div>
                                <Popover
                                    id={open ? "language-popover" : undefined}
                                    open={open}
                                    anchorEl={langSwitchElement}
                                    onClose={handlePopoverClose}
                                    anchorOrigin={{
                                        vertical: "bottom",
                                        horizontal: "center",
                                    }}
                                    transformOrigin={{
                                        vertical: "top",
                                        horizontal: "center",
                                    }}
                                >
                                    <PopoverContent>
                                        {languages
                                            .filter(item => item.code !== currentLanguage)
                                            .map((item) => (
                                            <p key={item.code} className={'cursor-pointer border-b-2 border-transparent hover:border-black'}
                                                onClick={() =>
                                                    handleLanguageChange(item.code)
                                                }
                                            >
                                                { item.selfname }
                                            </p>
                                        ))}
                                    </PopoverContent>
                                </Popover>
                            </div>

                            <ClientSwitcher />

                            <button
                                type="button"
                                ref={shoppingCartRef}
                                className="relative hover:opacity-70 bg-transparent focus:outline-none p-0 inline-flex cursor-pointer"
                                onClick={() => setIsShoppingCartOpen(prev => !prev)}
                            >
                                <ShoppingCartOutlinedIcon
                                    style={{
                                        color: children.props.client.brand_color_header_icons,
                                    }}
                                />

                                {shoppingCartProducts.length > 0 && (
                                    <div className="absolute -top-2 -right-2 size-4 bg-red-500 rounded-full flex items-center justify-center text-white text-xs">
                                        {shoppingCartProducts.length}
                                    </div>
                                )}
                            </button>
                            <Popover
                                open={isShoppingCartOpen}
                                anchorEl={shoppingCartRef.current}
                                onClose={() => setIsShoppingCartOpen(false)}
                                anchorOrigin={{
                                    vertical: "bottom",
                                    horizontal: "center",
                                }}
                                transformOrigin={{
                                    vertical: "top",
                                    horizontal: "right",
                                }}
                            >
                                <div className="flex flex-col w-[480px] h-[520px] max-h-full max-w-full overflow-auto bg-white">
                                    <div className="p-5 flex-1">
                                        {shoppingCartProducts.length > 0 ? (
                                            <ShoppingCartContainer variant="minimal" />
                                        ) : (
                                            <div className="flex flex-col gap-4 items-center justify-center h-full w-full">
                                                <ShoppingCartOutlinedIcon className="!size-20 text-secondary" />
                                                <div className="flex flex-col items-center justify-center gap-2">
                                                    <div className="text-xl font-bold">
                                                        <Trans i18nKey="shoppingCart.empty.title">No products in the shopping cart</Trans>
                                                    </div>
                                                    <div className="text-gray-600">
                                                        <Trans i18nKey="shoppingCart.empty.description">Visit the shop to purchase products</Trans>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    <div className="sticky bottom-0 left-0 w-full">
                                        <Button
                                            href={shoppingCartProducts.length === 0 ? route("partsshop.index") : route("partsshop.checkout")}
                                            variant="contained"
                                            color="secondary"
                                            className="w-full font-bold"
                                            onClick={() => setIsShoppingCartOpen(false)}
                                        >
                                            {shoppingCartProducts.length === 0 ? (
                                                <Trans i18nKey="shoppingCart.goToShop">Visit the shop</Trans>
                                            ) : (
                                                <Trans i18nKey="shoppingCart.goToCheckout">Complete Checkout</Trans>
                                            )}
                                        </Button>
                                    </div>
                                </div>
                            </Popover>

                            {children.props.user?.id && (
                                <InertiaLink
                                    href={route("profile")}
                                    style={{ lineHeight: 0 }}
                                    className={'ml-4 hover:opacity-70'}
                                >
                                    <Person
                                        style={{
                                            color: children.props.client
                                                .brand_color_header_icons,
                                        }}
                                    />
                                </InertiaLink>
                            )}
                            {children.props.user?.id && (
                                <ExitToApp
                                    onClick={handleLogoutClick}
                                    style={{
                                        color: children.props.client
                                            .brand_color_header_icons,
                                        cursor: "pointer",
                                    }}
                                    className={'hover:opacity-70'}
                                />
                            )}
                        </div>
                        <div className={'flex justify-center items-center gap-3 hidden lg:flex ml-9'}>
                            <div className={'text-sm text-white'}>
                                Powered by
                            </div>
                            <div className={'flex items-center justify-center flex-wrap lg:flex-nowrap gap-3'}>
                                {children.props.client.brand_logo && (
                                    <img
                                        className={'w-16 h-auto lg:w-auto lg:h-8 '}
                                        alt={'B2X'}
                                        src={
                                            "/images/logos/b2x_logo_white.svg"
                                        }
                                    />
                                )}
                            </div>
                        </div>
                    </Grid>
                </Container>
            </Nav>
            <div className={'bg-[#374559] py-3 text-white flex lg:hidden flex-col justify-center items-center text-center cursor-pointer hover:text-gray-200'}
                 onClick={() => {
                     setLangModalOpen(true);
                 }}
            >
                <p className={'text-xs mt-0 mb-0'}>NIU Scooter Repair</p>
                <p className={'flex gap-3 font-bold mt-0 mb-0'}>
                    <MapIcon />
                    <span>{ country.name }</span>
                </p>
            </div>
            <div className={'pt-12 flex-1'} style={wrapperStyle}>
                {children}
            </div>
            <button
                className="p-4 mx-0 mt-16 mb-0 w-full text-center text-white normal-case bg-none cursor-pointer bg-[#000]"
                onClick={() => {
                    document.getElementById("top").scrollIntoView({
                        behavior: "smooth",
                    });
                }}
            >
                <Trans i18nKey="layout.backToTop">Back to top</Trans>
            </button>

            <FooterLogos>
                <Grid container maxWidth="md" spacing={3} justifyContent="center">
                    <Grid item xs={12}>
                        <h2 className={'text-center text-2xl font-bold mb-8'}>
                            NIU Scooter Repair
                        </h2>
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <div className={'flex flex-col justify-center items-center'}>
                            <div>
                                An authorized Service of
                            </div>
                            {children.props.client.brand_logo && (
                                <img
                                    className={'w-auto h-14 mt-1 '}
                                    alt={'Client logo'}
                                    src={children.props.client.brand_logo}
                                />
                            )}
                        </div>
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <div className={'flex flex-col justify-center items-center'}>
                            <div>
                            Powered by
                            </div>
                            {children.props.client.brand_logo && (
                                <img
                                    className={'w-auto h-10 mt-3 '}
                                    alt={'B2X'}
                                    src={
                                        "/images/logos/b2x_logo_white.svg"
                                    }
                                />
                            )}
                        </div>
                    </Grid>
                </Grid>
            </FooterLogos>
            <Footer color={"" || children.props.client.brand_color_primary}>
                <Grid container maxWidth="md" spacing={3} justifyContent="center">
                    <Grid item xs={12}>
                        <ul className={'flex flex-col items-center list-none'}>
                            <li>
                                <InertiaLink className={'no-underline text-[#FAFAFA] hover:text-white text-center'} href={route('order')}><Trans i18nKey={'homeNiu.requestRepairButton'}>Request Repair</Trans></InertiaLink>
                            </li>
                            <li className={'mt-2'}>
                                <InertiaLink className={'no-underline text-[#FAFAFA] hover:text-white text-center'} href={route('track.index')}><Trans i18nKey={'homeNiu.checkRepairStatus'}>Check Repair Status</Trans></InertiaLink>
                            </li>
                            <li className={'mt-2'}>
                                <InertiaLink className={'no-underline text-[#FAFAFA] hover:text-white text-center'} href={route('termsandconditions')}><Trans i18nKey={'orderEntry.termsandconditions'}>Terms & Conditions</Trans></InertiaLink>
                            </li>
                            <li className={'mt-2'}>
                                <a className={'no-underline text-[#FAFAFA] hover:text-white text-center'} href={route('privacy')}><Trans i18nKey={'privacypolicy'}>Our Privacy Policy</Trans></a>
                            </li>
                            <li className={'mt-2'}>
                                <InertiaLink
                                    className={'no-underline text-[#FAFAFA] hover:text-white text-center'}
                                    href={route('imprint')}>
                                    <Trans i18nKey={'imprint'}>Imprint</Trans>
                                </InertiaLink>
                            </li>
                            <li className={'mt-2'}>
                                <InertiaLink className={'no-underline text-[#FAFAFA] hover:text-white text-center'} href={route('login')}><Trans i18nKey={'login@niu'}>Login</Trans></InertiaLink>
                            </li>
                            <li className={'mt-6'}>
                                <a className={'no-underline text-[#FAFAFA] hover:text-white text-center text-xs'} href={'https://b2x.com'}>Copyright © 2024 B2X Care Solutions GmbH</a>
                            </li>
                        </ul>
                    </Grid>
                </Grid>
            </Footer>
            {
                langModalOpen && (
                    <LanguageModal setLangModalOpen={setLangModalOpen} />
                )
            }
            {flash.status && (
                <FlashMessage message={flash.status} />
            )}
            {flash.message && (
                <FlashMessage message={flash.message} />
            )}
            {flash.warning && (
                <FlashMessage message={flash.warning} type={'warning'} />
            )}
        </div>
    );
}
